import { z } from 'zod';

import type {
  DescribeGuessInput,
  DescribePenalties,
  DescribePickCategoryInput,
  DescribePickWordInput,
  DescribePlayerView,
  DescribeSecretView,
  DescribeSetPinInput,
} from './describe-word.js';
import type { Ack } from './errors.js';
import type { SocketError } from './errors.js';
import type {
  JoinResult,
  PlayerState,
  PlayerView,
  SessionSnapshot,
  SessionStatus,
  TeamView,
} from './lobby.js';
import type {
  BoardMoved,
  BoardShotPayload,
  ItemResult,
  PlayerItemView,
  RoundProgress,
  RoundStarted,
  RoundSummary,
  SessionFinished,
  SubmitResult,
  TeamRoundOutcome,
} from './round.js';

/**
 * The single definition of every socket event (§13).
 *
 * Client and server both import these maps, so renaming an event is a compile
 * error on both sides rather than a runtime mystery in a classroom.
 *
 * Phase 3 covers the lobby. Round and item events (§9.2, §9.3) arrive with the
 * gameplay loop in phase 4 and extend these same maps.
 */

export const PLAY_NAMESPACE = '/play';
export const ADMIN_NAMESPACE = '/admin';
export const REMOTE_NAMESPACE = '/remote';

export const sessionRoom = (sessionId: string): string => `session:${sessionId}`;
export const teamRoom = (teamId: string): string => `team:${teamId}`;
export const playerRoom = (playerId: string): string => `player:${playerId}`;

/** Handshake `auth` for `/admin`: identity comes from the session cookie. */
export const adminHandshakeSchema = z.object({
  sessionId: z.string().uuid(),
});

export type AdminHandshake = z.infer<typeof adminHandshakeSchema>;

/* ---------------------------------------------------------------
 * /play -- players. Never carries an answer key, a roster, or any
 * teacher-facing payload; that is what the namespace split buys.
 * ------------------------------------------------------------- */

export interface PlayServerToClientEvents {
  'player:state': (state: PlayerState) => void;
  /** Late joiner awaiting a team (D-18). */
  'player:unassigned': () => void;
  'session:status': (payload: { status: SessionStatus }) => void;
  /** The teacher removed this player, or the session ended. */
  'player:removed': (payload: { reason: string }) => void;

  /** The next question. Structurally cannot carry an answer key (§9.5). */
  'item:show': (item: PlayerItemView) => void;
  /** After this player submits, or after their deadline passes. */
  'item:result': (result: ItemResult) => void;
  /** Between items, or waiting on teammates to finish. */
  'round:waiting': (payload: { answered: number; total: number }) => void;
  'round:summary': (summary: RoundSummary) => void;
  /** Session ended (finish line or teacher ended early). Carries team standings. */
  'session:finished': (payload: SessionFinished) => void;

  /**
   * The teacher froze or unfroze the round (D-17).
   *
   * `session:resumed` carries the item again, with its deadline re-stamped, so
   * a student does not have to ask for it back.
   */
  'session:paused': (payload: Record<string, never>) => void;
  'session:resumed': (payload: { item: PlayerItemView | null }) => void;

  /**
   * The round wall clock. Pushed at round start, after a pause is lifted, and
   * cleared (`closesAt: null`) when the round ends or is frozen.
   */
  'round:clock': (payload: { closesAt: number | null }) => void;

  /**
   * Lýstu orðinu: the speaker picks a category, then a word; guessers see
   * options without a correctIndex so a phone cannot leak the answer.
   */
  'describe:view': (view: DescribePlayerView) => void;

  error: (error: SocketError) => void;
}

export interface PlayClientToServerEvents {
  'player:join': (
    input: { code: string; displayName: string },
    ack: (result: Ack<JoinResult>) => void,
  ) => void;
  'player:reconnect': (input: { token: string }, ack: (result: Ack<JoinResult>) => void) => void;

  /**
   * Submits an answer.
   *
   * The ack carries the graded result, so a student sees feedback without
   * waiting for a broadcast. Rejected if the item is not theirs, its deadline
   * has passed, or they have already answered it (§9.5).
   */
  'item:submit': (
    input: { itemId: string; payload: unknown },
    ack: (result: Ack<SubmitResult>) => void,
  ) => void;

  /**
   * Legacy: ends a first-question demo. Kept so older clients still ack;
   * new clients never call it because demos are no longer shown.
   *
   * Idempotent: if the issue already exists, returns the live view with the
   * existing deadline rather than minting a fresh window.
   */
  'item:ready': (input: { itemId: string }, ack: (result: Ack<PlayerItemView>) => void) => void;

  /**
   * Notes that this player started answering the item on screen — first
   * keystroke, or first placed word on orðaröð.
   *
   * Tölfræði uses it to tell a timeout while working from an idle phone that
   * never typed. Idempotent; later keystrokes are ignored. Best-effort: a
   * late or dropped call must not block answering.
   */
  'item:engaged': (
    input: { itemId: string },
    ack: (result: Ack<{ recorded: true }>) => void,
  ) => void;

  /**
   * Re-requests the current item after a reconnect, or after one expired.
   *
   * A student whose wifi dropped mid-item needs the question back without the
   * round waiting for them, and a student whose deadline passed needs to be
   * moved off a question that is no longer accepting answers. Returns null when
   * there is nothing live: every item of theirs is answered or expired.
   */
  'item:current': (
    input: Record<string, never>,
    ack: (result: Ack<PlayerItemView | null>) => void,
  ) => void;

  /**
   * Reads the server's clock, so a client can draw an honest countdown (§9.3).
   *
   * Deadlines cross the wire as absolute server timestamps. A device whose
   * clock is a few seconds off would otherwise count toward the wrong moment --
   * showing time that has already gone, or ending a question early -- and a
   * student would be answering into a window the server has closed.
   *
   * It buys nobody extra time: `elapsedMs` and the late check are computed from
   * the server's own receipt time regardless (§9.5).
   */
  'time:sync': (
    input: Record<string, never>,
    ack: (result: Ack<{ serverNow: number }>) => void,
  ) => void;

  /**
   * Leaves for good, rather than merely disconnecting.
   *
   * A disconnect is assumed to be accidental and is held through a grace
   * period (D-14). Leaving is deliberate, so it releases the roster slot and
   * the name immediately -- otherwise the teacher is left looking at a student
   * who is never coming back.
   */
  'player:leave': (
    input: Record<string, never>,
    ack: (result: Ack<{ left: true }>) => void,
  ) => void;

  /** Last mark wins until the server clock hits zero. */
  'describe:guess': (
    input: DescribeGuessInput,
    ack: (result: Ack<{ marked: string }>) => void,
  ) => void;

  /** Speaker: choose one of the two offered categories. */
  'describe:pick-category': (
    input: DescribePickCategoryInput,
    ack: (result: Ack<{ picked: true }>) => void,
  ) => void;

  /** Speaker: choose one of the two offered words; starts the 60s clock. */
  'describe:pick-word': (
    input: DescribePickWordInput,
    ack: (result: Ack<{ picked: true }>) => void,
  ) => void;
}

/* ---------------------------------------------------------------
 * /admin -- the teacher. Authenticated by the same session cookie
 * as the HTTP surface, and scoped to sessions they own.
 * ------------------------------------------------------------- */

export interface AdminServerToClientEvents {
  /** Full snapshot on connect and reconnect; the client owns no state (D-14). */
  'session:state': (snapshot: SessionSnapshot) => void;
  'session:status': (payload: { status: SessionStatus; roundOrdinal?: number }) => void;
  'player:joined': (payload: { player: PlayerView }) => void;
  'player:updated': (payload: { player: PlayerView }) => void;
  'player:connection': (payload: { playerId: string; connected: boolean }) => void;
  'player:left': (payload: { playerId: string }) => void;
  'team:updated': (payload: { team: TeamView }) => void;
  'team:removed': (payload: { teamId: string }) => void;

  'round:started': (payload: RoundStarted) => void;
  'round:clock': (payload: { closesAt: number | null }) => void;
  /**
   * Throttled and aggregated, never per-response (§9.2).
   *
   * At 300 players a naive per-response broadcast is 300 events per item per
   * session, all of them landing in the same second when a timer expires.
   */
  'round:progress': (payload: RoundProgress) => void;
  /** `roundId` is what the per-question review is fetched against. */
  'round:closed': (payload: {
    roundId: string;
    roundOrdinal: number;
    results: TeamRoundOutcome[];
  }) => void;
  'board:moved': (payload: BoardMoved) => void;
  'board:shot': (payload: BoardShotPayload) => void;
  'session:finished': (payload: SessionFinished) => void;

  error: (error: SocketError) => void;
}

export interface AdminClientToServerEvents {
  'team:assign': (
    input: { playerId: string; teamId: string | null },
    ack: (result: Ack<{ player: PlayerView }>) => void,
  ) => void;
  'team:create': (input: { name?: string }, ack: (result: Ack<{ team: TeamView }>) => void) => void;
  'team:rename': (
    input: { teamId: string; name: string },
    ack: (result: Ack<{ team: TeamView }>) => void,
  ) => void;
  'team:remove': (
    input: { teamId: string },
    ack: (result: Ack<{ teamId: string }>) => void,
  ) => void;
  /**
   * Deals the whole roster back out across the teams, as evenly as it divides.
   *
   * Acknowledged with the snapshot rather than with a list of players: thirty
   * students move at once, and one re-read is both smaller than thirty deltas
   * and impossible to apply half of (D-14).
   */
  'team:shuffle': (
    input: Record<string, never>,
    ack: (result: Ack<SessionSnapshot>) => void,
  ) => void;
  'player:rename': (
    input: { playerId: string; displayName: string },
    ack: (result: Ack<{ player: PlayerView }>) => void,
  ) => void;
  'player:remove': (
    input: { playerId: string },
    ack: (result: Ack<{ playerId: string }>) => void,
  ) => void;
  /**
   * Re-requests the snapshot without reconnecting the socket.
   *
   * Takes an empty payload it does not need, so that every acknowledged event
   * has the same `(input, ack)` shape and one wrapper can handle them all.
   */
  'session:sync': (
    input: Record<string, never>,
    ack: (result: Ack<SessionSnapshot>) => void,
  ) => void;

  /* -------------------------------------------------------------
   * Running the game (D-17). The teacher starts every round; the
   * game never advances on its own.
   * ----------------------------------------------------------- */

  /** LOBBY -> INTERMISSION. Snapshots the board into the session (§8.1). */
  'session:start': (
    input: Record<string, never>,
    ack: (result: Ack<SessionSnapshot>) => void,
  ) => void;

  /** INTERMISSION -> IN_PROGRESS. */
  'round:start': (input: Record<string, never>, ack: (result: Ack<RoundStarted>) => void) => void;

  /**
   * IN_PROGRESS <-> PAUSED (D-17).
   *
   * Freezes every deadline; resuming re-stamps them from the time each item had
   * left rather than from its original absolute value.
   */
  'session:pause': (
    input: Record<string, never>,
    ack: (result: Ack<{ status: SessionStatus }>) => void,
  ) => void;
  'session:resume': (
    input: Record<string, never>,
    ack: (result: Ack<{ status: SessionStatus }>) => void,
  ) => void;

  /** Ends the game early; the dashboard is still shown (§8.1). */
  'session:end': (
    input: Record<string, never>,
    ack: (result: Ack<SessionFinished>) => void,
  ) => void;

  /**
   * Finndu fjögur: the teacher spends this team's movement budget.
   * `right`/`left`/`up`/`down` are steps; opposing axes are ignored.
   */
  'findfour:move': (
    input: { right: number; left: number; up: number; down: number },
    ack: (result: Ack<SessionSnapshot>) => void,
  ) => void;

  /** After a bow upgrade, whether and where this team shoots. */
  'findfour:bow': (
    input: { shoot: boolean; axis?: 'up' | 'down' | 'left' | 'right' },
    ack: (result: Ack<SessionSnapshot>) => void,
  ) => void;

  /** The board and the results the dashboard renders, on demand. */
  'session:results': (
    input: Record<string, never>,
    ack: (result: Ack<SessionFinished>) => void,
  ) => void;

  /** Lýstu orðinu: offer the current speaker two categories to choose from. */
  'describe:start': (
    input: Record<string, never>,
    ack: (result: Ack<SessionSnapshot>) => void,
  ) => void;
  /** No movement; skip to the next speaker. */
  'describe:cancel': (
    input: Record<string, never>,
    ack: (result: Ack<SessionSnapshot>) => void,
  ) => void;
  /** Same speaker, two new random categories. */
  'describe:reset': (
    input: Record<string, never>,
    ack: (result: Ack<SessionSnapshot>) => void,
  ) => void;
  /** Teacher types a 3-digit PIN for the secret console. */
  'describe:set-pin': (
    input: DescribeSetPinInput,
    ack: (result: Ack<SessionSnapshot>) => void,
  ) => void;
  /** Start the 60s clock after the speaker has picked a word. */
  'describe:begin': (
    input: Record<string, never>,
    ack: (result: Ack<SessionSnapshot>) => void,
  ) => void;
  /** Skip this speaker and start the next connected student. */
  'describe:skip': (
    input: Record<string, never>,
    ack: (result: Ack<SessionSnapshot>) => void,
  ) => void;
  /** End the describing clock early; scoring follows as if the timer ran out. */
  'describe:end': (
    input: Record<string, never>,
    ack: (result: Ack<SessionSnapshot>) => void,
  ) => void;
}

/** Handshake `auth` for `/remote`: cookie proves the PIN login; token matches the QR. */
export const remoteHandshakeSchema = z.object({
  token: z.string().min(8).max(128),
});

export type RemoteHandshake = z.infer<typeof remoteHandshakeSchema>;

export interface RemoteServerToClientEvents {
  'describe:secret': (view: DescribeSecretView) => void;
  error: (error: SocketError) => void;
}

export interface RemoteClientToServerEvents {
  'describe:penalties': (
    input: DescribePenalties,
    ack: (result: Ack<{ saved: true }>) => void,
  ) => void;
  'describe:confirm': (
    input: Record<string, never>,
    ack: (result: Ack<{ confirmed: true }>) => void,
  ) => void;
  'describe:end': (
    input: Record<string, never>,
    ack: (result: Ack<{ ended: true }>) => void,
  ) => void;
}
